import { NgClass, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { KENDO_POPUP } from '@progress/kendo-angular-popup';
import { aosColor as colors, IconComponent, IconSize } from '@ui/aos-lib';

import { RouteItem } from '../../../../routes/app.types';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    KENDO_POPUP,
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    UpperCasePipe
  ],
  selector: 'aos-side-menu-collapsed',
  styleUrl: './side-menu-collapsed.component.scss',
  templateUrl: `./side-menu-collapsed.component.html`
})
export class SideMenuCollapsedComponent implements OnInit {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) menuItems: readonly RouteItem[] = [];

  protected readonly itemIconColor = colors.primary300;
  protected readonly itemIconSize = IconSize.MD;
  protected readonly selectedItemIconColor = colors.primary100;

  protected currentSelectedItem: RouteItem | null = null;
  protected showPopup = false;
  protected activePopupItem: RouteItem | null = null;
  private lastPointerType = '';

  ngOnInit(): void {
    // Close popup when tapping outside the menu on touch devices,
    // where pointerleave is suppressed and won't dismiss the popup.
    const onDocumentClick = (): void => {
      if (this.showPopup) {
        this.showPopup = false;
        this.activePopupItem = null;
        this.cdr.markForCheck();
      }
    };
    document.addEventListener('click', onDocumentClick);
    this.destroyRef.onDestroy(() => document.removeEventListener('click', onDocumentClick));
  }

  protected getClassBindings(menuItem: RouteItem, isContextMenuItem: boolean): Record<string, boolean> {
    return {
      'aos-side-menu-item': !isContextMenuItem,
      'aos-side-menu-item_selected': !isContextMenuItem && (menuItem.hasSelectedChild || menuItem.selected),
      'aos-side-menu-subitem': isContextMenuItem,
      'aos-side-menu-subitem_selected': isContextMenuItem && menuItem.selected
    };
  }

  protected handleClick(evt: MouseEvent, menuItem: RouteItem): void {
    // Touch: show children popup and let RouterLink navigate to the parent route.
    // stopPropagation prevents the document click listener from immediately closing the popup.
    if (menuItem.children.length && this.lastPointerType === 'touch') {
      evt.stopPropagation();
      this.showPopup = true;
      this.activePopupItem = menuItem;
      menuItem.setSelectedState(true);
      if (this.currentSelectedItem !== menuItem) {
        this.currentSelectedItem?.setSelectedState(false);
        this.currentSelectedItem = menuItem;
      }
      return;
    }
    this.selectItem(evt, menuItem);
    if (!menuItem.hasExternalLink && this.currentSelectedItem !== menuItem) {
      this.currentSelectedItem?.setSelectedState(false);
      menuItem.setSelectedState(true);
      this.currentSelectedItem = menuItem;
    }
  }

  protected selectItem(evt: MouseEvent, menuItem: RouteItem): void {
    evt.stopPropagation();
    menuItem.setSelectedState(true);
    this.showPopup = false;
    this.activePopupItem = null;
  }

  // On touch devices, pointerenter/pointerleave fire on tap-down/tap-up instantly,
  // making hover-based popups unusable. Skip hover logic for touch — handleClick manages the popup instead.
  protected onMouseEnter(evt: MouseEvent, item: RouteItem): void {
    this.lastPointerType = (evt as PointerEvent).pointerType ?? '';
    if (!item.children.length || this.lastPointerType === 'touch') {
      return;
    }
    this.showPopup = true;
    this.activePopupItem = item;
  }

  protected onMouseLeave(evt: MouseEvent, _item?: RouteItem): void {
    if ((evt as PointerEvent).pointerType === 'touch') {
      return;
    }
    const target = evt.target as HTMLElement;
    if ((evt.relatedTarget as HTMLElement)?.contains(target)) {
      return;
    }
    this.showPopup = false;
    this.activePopupItem = null;
  }
}
